
import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import { Memory } from '@/types/memory';
import { Card } from '@/components/ui/card';
import MemoryGrid from '@/components/memories/MemoryGrid';
import MemoryUploader from '@/components/memories/MemoryUploader';
import { useToast } from '@/hooks/use-toast';
import { Trip } from '@/types/itinerary';

interface TripContextType {
  trip: Trip;
  isAuthorized: boolean;
}

const Memories: React.FC = () => {
  const { user, isGuest } = useAuth();
  const { toast } = useToast();
  const { tripId: tripIdFromParams } = useParams<{ tripId: string }>();
  const context = useOutletContext<TripContextType | null>();
  
  const [memories, setMemories] = useState<Memory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use trip ID from context if available, otherwise fallback to URL params
  const tripId = context?.trip?.id || tripIdFromParams || "123e4567-e89b-12d3-a456-426614174000";
  
  // Check if we're in trip layout
  const inTripLayout = !!context;

  useEffect(() => {
    fetchMemories();

    // Set up real-time subscription for new memories
    const memoriesChannel = supabase
      .channel('memories-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'memories',
          filter: `trip_id=eq.${tripId}`
        },
        () => fetchMemories()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'memory_likes',
        },
        () => fetchMemories()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(memoriesChannel);
    };
  }, [tripId]);

  const fetchMemories = async () => {
    try {
      setIsLoading(true);
      
      // Fetch memories with likes count
      const { data, error } = await supabase
        .from('memories')
        .select(`
          *,
          likes:memory_likes(*)
        `)
        .eq('trip_id', tripId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process data to include like count and user like status
      const processedMemories = data.map((memory) => {
        const likes = memory.likes || [];
        const userHasLiked = user ? likes.some((like: any) => like.user_id === user.id) : false;
        
        return {
          ...memory,
          likes,
          like_count: likes.length,
          user_has_liked: userHasLiked
        };
      });

      setMemories(processedMemories);
    } catch (error) {
      console.error('Error fetching memories:', error);
      toast({
        title: "Error",
        description: "Failed to load memories",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMemoryUploaded = () => {
    fetchMemories();
    toast({
      title: "Success",
      description: "Memory uploaded successfully",
    });
  };

  const handleMemoryDeleted = () => {
    fetchMemories();
  };

  return (
    <div className={inTripLayout ? "" : "container mx-auto px-4 py-8"}>
      <h2 className={`text-2xl font-bold ${inTripLayout ? "text-[#0A3D62]" : "text-navteal"} font-poppins mb-4`}>
        Trip Memories
      </h2>
      
      {(!isGuest && user) && (
        <Card className="mb-8 p-6">
          <h3 className="text-xl font-semibold text-navteal mb-4">Add New Memory</h3>
          <MemoryUploader 
            tripId={tripId} 
            onSuccess={handleMemoryUploaded} 
          />
        </Card>
      )}
      
      <MemoryGrid 
        memories={memories} 
        isLoading={isLoading} 
        userCanLike={!!user}
        onLikeToggle={fetchMemories}
        onDeleteMemory={handleMemoryDeleted}
      />
    </div>
  );
};

export default Memories;
